import { useMemo } from 'react';
import { SimulatorCartItem, TimePeriod, SimulatorSummary, CustomPeriodMultipliers, CustomPointValues } from '../types';
import { PERIOD_MULTIPLIERS } from '../types';
import { calculateActRevenueEvolution, RevenueEvolution } from '../../../utils/simulatorCalculations';
import { getDisplacementValue } from '../../../utils/pricingUtils';

interface UseSimulatorCalculationsReturn {
  summary: SimulatorSummary;
  itemEvolutions: Map<string, RevenueEvolution>;
}

export const useSimulatorCalculations = (
  cartItems: SimulatorCartItem[],
  selectedPeriod: TimePeriod,
  isDrom: boolean,
  customMultipliers?: CustomPeriodMultipliers,
  customPointValues?: CustomPointValues
): UseSimulatorCalculationsReturn => {
  
  // Calculate evolution for each cart item
  const itemEvolutions = useMemo(() => {
    const evolutionMap = new Map<string, RevenueEvolution>();

    cartItems.forEach(item => {
      const evolution = calculateActRevenueEvolution(
        item.act,
        item.quantity,
        selectedPeriod,
        isDrom,
        item.homeVisitPercentage,
        customMultipliers,
        customPointValues,
        item.balneotherapy
      );
      evolutionMap.set(item.id, evolution);
    });

    return evolutionMap;
  }, [cartItems, selectedPeriod, isDrom, customMultipliers, customPointValues]);

  // Calculate summary totals
  const summary = useMemo((): SimulatorSummary => {
    let totalOriginalRevenue = 0;
    let totalCurrentRevenue = 0;
    let totalFutureRevenueJanv2026 = 0;
    let totalFutureRevenueMay2026 = 0;
    let totalFutureRevenueSept2026 = 0;
    let totalFutureRevenue2027 = 0;
    let totalHomeVisitRevenue = 0;
    let actsWithHomeVisitsCount = 0;
    let totalDailyEquivalentQuantity = 0;
    let hasOriginalValues = false;

    cartItems.forEach((item) => {
      const evolution = itemEvolutions.get(item.id);
      if (!evolution) return;
      
      // Accumulate daily quantities
      totalDailyEquivalentQuantity += item.quantity;
      
      // Calculate home visit revenue if applicable
      if (item.homeVisitPercentage > 0) {
        actsWithHomeVisitsCount++;
        const multipliers = customMultipliers || PERIOD_MULTIPLIERS;
        const totalQuantityForPeriod = item.quantity * multipliers[selectedPeriod];
        const displacementValue = getDisplacementValue(item.act.indemnite_deplacement);
        const homeVisitRevenue = displacementValue * (item.homeVisitPercentage / 100) * totalQuantityForPeriod;
        totalHomeVisitRevenue += homeVisitRevenue;
      }
      
      if (evolution.original.value > 0) {
        totalOriginalRevenue += evolution.original.value;
        hasOriginalValues = true;
      }
      totalCurrentRevenue += evolution.current.value;

      const now = new Date();

      // Pour chaque horizon, n'utiliser le slot que si sa date est dans le futur
      const futureValueJanv2026 = (evolution.janv2026 && evolution.janv2026.date > now)
        ? evolution.janv2026.value
        : evolution.current.value;
      totalFutureRevenueJanv2026 += futureValueJanv2026;

      const futureValueMay2026 = (evolution.may2026 && evolution.may2026.date > now)
        ? evolution.may2026.value
        : (evolution.janv2026 && evolution.janv2026.date > now)
          ? evolution.janv2026.value
          : evolution.current.value;
      totalFutureRevenueMay2026 += futureValueMay2026;

      const futureValueSept2026 = (evolution.september2026 && evolution.september2026.date > now)
        ? evolution.september2026.value
        : (evolution.may2026 && evolution.may2026.date > now)
          ? evolution.may2026.value
          : (evolution.janv2026 && evolution.janv2026.date > now)
            ? evolution.janv2026.value
            : evolution.current.value;
      totalFutureRevenueSept2026 += futureValueSept2026;

      const futureValue2027 = (evolution.july2027 && evolution.july2027.date > now)
        ? evolution.july2027.value
        : (evolution.september2026 && evolution.september2026.date > now)
          ? evolution.september2026.value
          : (evolution.may2026 && evolution.may2026.date > now)
            ? evolution.may2026.value
            : (evolution.janv2026 && evolution.janv2026.date > now)
              ? evolution.janv2026.value
              : evolution.current.value;
      totalFutureRevenue2027 += futureValue2027;
    });

    // Convert total daily quantity to selected period
    const multipliers = customMultipliers || PERIOD_MULTIPLIERS;
    const totalQuantityInSelectedPeriod = totalDailyEquivalentQuantity * multipliers[selectedPeriod];

    const totalDifferenceJanv2026 = totalFutureRevenueJanv2026 - totalCurrentRevenue;
    const totalPercentageChangeJanv2026 = totalCurrentRevenue > 0 
      ? (totalDifferenceJanv2026 / totalCurrentRevenue) * 100 
      : 0;
      
    const totalDifferenceMay2026 = totalFutureRevenueMay2026 - totalCurrentRevenue;
    const totalPercentageChangeMay2026 = totalCurrentRevenue > 0
      ? (totalDifferenceMay2026 / totalCurrentRevenue) * 100 
      : 0;
      
    const totalDifferenceSept2026 = totalFutureRevenueSept2026 - totalCurrentRevenue;
    const totalPercentageChangeSept2026 = totalCurrentRevenue > 0 
      ? (totalDifferenceSept2026 / totalCurrentRevenue) * 100 
      : 0;
      
    // Calculate 2027 difference vs most recent 2026 value (not vs current)
    const totalDifference2027 = totalFutureRevenue2027 - totalFutureRevenueSept2026;
    const totalPercentageChange2027 = totalFutureRevenueSept2026 > 0 
      ? (totalDifference2027 / totalFutureRevenueSept2026) * 100 
      : 0;
    
    return {
      totalOriginalRevenue: Math.round(totalOriginalRevenue * 100) / 100,
      totalCurrentRevenue: Math.round(totalCurrentRevenue * 100) / 100,
      totalFutureRevenueJanv2026: Math.round(totalFutureRevenueJanv2026 * 100) / 100,
      totalDifferenceJanv2026: Math.round(totalDifferenceJanv2026 * 100) / 100,
      totalPercentageChangeJanv2026: Math.round(totalPercentageChangeJanv2026 * 100) / 100,
      totalFutureRevenueMay2026: Math.round(totalFutureRevenueMay2026 * 100) / 100,
      totalDifferenceMay2026: Math.round(totalDifferenceMay2026 * 100) / 100,
      totalPercentageChangeMay2026: Math.round(totalPercentageChangeMay2026 * 100) / 100,
      totalFutureRevenueSept2026: Math.round(totalFutureRevenueSept2026 * 100) / 100,
      totalDifferenceSept2026: Math.round(totalDifferenceSept2026 * 100) / 100,
      totalPercentageChangeSept2026: Math.round(totalPercentageChangeSept2026 * 100) / 100,
      totalFutureRevenue2027: Math.round(totalFutureRevenue2027 * 100) / 100,
      totalDifference2027: Math.round(totalDifference2027 * 100) / 100,
      totalPercentageChange2027: Math.round(totalPercentageChange2027 * 100) / 100,
      itemCount: cartItems.length,
      hasOriginalValues,
      totalHomeVisitRevenue: Math.round(totalHomeVisitRevenue * 100) / 100,
      actsWithHomeVisitsCount,
      totalQuantityInSelectedPeriod: Math.round(totalQuantityInSelectedPeriod)
    };
  }, [itemEvolutions, cartItems, selectedPeriod, customMultipliers]);

  return {
    summary,
    itemEvolutions
  };
};